import { Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { debounceTime, Subject, Subscription, takeUntil } from 'rxjs';
import {
  CardService,
  CardTransactionType,
} from '../../services/card.service';
import { RainCardTransaction } from '../../models/card-transaction.model';
import { generatePagination } from '../../utils/pagination.utils';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-card-transactions-list',
  standalone: false,
  templateUrl: './card-transactions-list.component.html',
  styleUrl: './card-transactions-list.component.css'
})
export class CardTransactionsListComponent {
    @Input() uuid!: string;
    cardTransactions: RainCardTransaction[] = [];
    nextPageUrl: string | null = null;
    previousPageUrl: string | null = null;
    currentPage = 1;
    totalPages = 1;
    pageNumbers: any = [];
    submitted = false;
    errorMessage = '';

    readonly transactionTypes: CardTransactionType[] = [
      'CARD_PAYMENT',
      'CARD_FUNDING',
      'CARD_REFUND',
      'CARD_WITHDRAWAL',
    ];

    txUuid = '';
    startDate = '';
    endDate = '';
    transactionType: CardTransactionType | '' = '';
    merchant = '';

    private textFilterSubject = new Subject<void>();
    private destroy$ = new Subject<void>();
    private loadSubscription?: Subscription;

    constructor(
      private cardService: CardService,
      private route: ActivatedRoute,
      private router: Router,
    ) {}

    ngOnInit() {
      const params = this.route.snapshot.queryParamMap;
      const pageParam = params.get('page');
      const parsedPage = pageParam ? Number(pageParam) : 1;
      this.currentPage = isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;

      this.txUuid = params.get('txUuid') ?? '';
      this.startDate = params.get('startDate') ?? '';
      this.endDate = params.get('endDate') ?? '';
      this.merchant = params.get('merchant') ?? '';
      const type = params.get('transactionType') as CardTransactionType | null;
      this.transactionType =
        type && this.transactionTypes.includes(type) ? type : '';

      this.textFilterSubject
        .pipe(debounceTime(300), takeUntil(this.destroy$))
        .subscribe(() => this.applyFilters());

      this.loadCardTransactions(this.currentPage);
    }

    ngOnDestroy(): void {
      this.loadSubscription?.unsubscribe();
      this.destroy$.next();
      this.destroy$.complete();
    }

    get uuidInvalid(): boolean {
      const value = this.txUuid.trim();
      return value !== '' && !UUID_REGEX.test(value);
    }

    get hasFilters(): boolean {
      return Boolean(
        this.txUuid ||
          this.startDate ||
          this.endDate ||
          this.transactionType ||
          this.merchant,
      );
    }

    reset() {
      this.cardTransactions = [];
      this.nextPageUrl = null;
      this.previousPageUrl = null;
      this.totalPages = 1;
      this.pageNumbers = [];
    }

    loadCardTransactions(page: number) {
      // A partially typed UUID would only produce a 400; wait until it's complete.
      if (this.uuidInvalid) return;

      this.reset();
      this.submitted = true;
      this.errorMessage = '';
      this.currentPage = page;
      this.syncFiltersToUrl(page);

      // Drop any in-flight request so a slower, stale response can't overwrite this one.
      this.loadSubscription?.unsubscribe();
      this.loadSubscription = this.cardService
        .getCardTransactions(this.uuid, page, {
          uuid: this.txUuid,
          startDate: this.startDate,
          endDate: this.endDate,
          transactionType: this.transactionType,
          merchant: this.merchant,
        })
        .subscribe({
          next: (response) => {
            this.cardTransactions = response.results;
            this.nextPageUrl = response.next;
            this.previousPageUrl = response.previous;
            this.currentPage = page;

            const itemsPerPage = 20;
            this.totalPages = Math.ceil(response.count / itemsPerPage);
            this.pageNumbers = generatePagination(this.currentPage, this.totalPages);
            this.submitted = false;
          },
          error: (err: HttpErrorResponse) => {
            this.submitted = false;
            this.errorMessage =
              err.status === 400 && err.error?.error
                ? err.error.error
                : 'Failed to load card transactions';
          },
        });
    }

    onTextFilterChange() {
      this.textFilterSubject.next();
    }

    applyFilters() {
      this.loadCardTransactions(1);
    }

    clearFilters() {
      this.txUuid = '';
      this.startDate = '';
      this.endDate = '';
      this.transactionType = '';
      this.merchant = '';
      this.applyFilters();
    }

    private syncFiltersToUrl(page: number = this.currentPage) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          page: page > 1 ? page : null,
          txUuid: this.txUuid.trim() || null,
          startDate: this.startDate || null,
          endDate: this.endDate || null,
          transactionType: this.transactionType || null,
          merchant: this.merchant.trim() || null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    goToNextPage() {
      if (this.nextPageUrl) {
        const nextPage = this.getPageNumber(this.nextPageUrl);
        this.loadCardTransactions(nextPage);
      }
    }

    goToPreviousPage() {
      if (this.previousPageUrl) {
        const prevPage = this.getPageNumber(this.previousPageUrl);
        this.loadCardTransactions(prevPage);
      }
    }

    private getPageNumber(url: string): number {
      if (!url) return 1; // fallback if url is null

      const queryString = url.split('?')[1]; // get everything after "?"
      if (!queryString) return 1;

      const params = new URLSearchParams(queryString);
      const page = params.get('page');

      return page ? Number(page) : 1;
    }
}
